import type { Locale } from './types';

type Entry = readonly [string, string, string];

/**
 * The first value is Simplified Chinese, the second is Traditional Chinese
 * with Hong Kong wording, and the third is English.
 */
export const translationEntries = {
  'app.title': ['记忆的缝隙', '記憶的縫隙', 'The Erasure of Me'],
  'app.description': [
    '《记忆的缝隙》——关于记忆、尊严与陪伴的 2D 叙事解谜游戏。',
    '《記憶的縫隙》——關於記憶、尊嚴與陪伴的 2D 敘事解謎遊戲。',
    'The Erasure of Me — a 2D narrative puzzle game about memory, dignity, and companionship.',
  ],
  'app.skip_to_menu': ['跳到游戏菜单', '跳到遊戲選單', 'Skip to game menu'],
  'app.aria.game': ['记忆的缝隙游戏', '記憶的縫隙遊戲', 'The Erasure of Me game'],
  'app.aria.scene': ['游戏场景', '遊戲場景', 'Game scene'],
  'app.aria.canvas': ['可操作游戏画面', '可操作遊戲畫面', 'Interactive game canvas'],
  'app.aria.touch_controls': ['触屏游戏控制', '觸控遊戲控制', 'Touch game controls'],
  'app.aria.direction': ['移动方向', '移動方向', 'Movement direction'],
  'touch.up': ['向上移动', '向上移動', 'Move up'],
  'touch.down': ['向下移动', '向下移動', 'Move down'],
  'touch.left': ['向左移动', '向左移動', 'Move left'],
  'touch.right': ['向右移动', '向右移動', 'Move right'],
  'touch.observe': ['按住静静留意', '按住靜靜留意', 'Hold to quietly notice'],
  'touch.observe.short': ['留意', '留意', 'Notice'],
  'touch.interact': ['与附近物件交互', '與附近物件互動', 'Interact with nearby object'],
  'touch.interact.short': ['交互', '互動', 'Interact'],
  'touch.pause': ['暂停游戏', '暫停遊戲', 'Pause game'],
  'touch.pause.short': ['暂停', '暫停', 'Pause'],
  'common.interact': ['交互', '互動', 'Interact'],
  'common.interact_with': ['与{label}交互', '與{label}互動', 'Interact with {label}'],
  'common.action_with': ['{verb}{label}', '{verb}{label}', '{verb}: {label}'],
  'common.continue': ['继续', '繼續', 'Continue'],
  'common.inspect': ['查看', '查看', 'Inspect'],
  'common.pickup': ['拾取', '拾取', 'Pick up'],
  'common.go': ['前往', '前往', 'Go'],
  'common.place': ['放置', '放置', 'Place'],
  'common.hold': ['牵手', '牽手', 'Take her hand'],
  'common.hold.long': ['按住牵手', '按住牽手', 'Hold to take her hand'],
  'common.close': ['关闭', '關閉', 'Close'],
  'common.back': ['返回', '返回', 'Back'],
  'common.confirm': ['确认', '確認', 'Confirm'],
  'common.cancel': ['取消', '取消', 'Cancel'],
  'common.delete': ['删除', '刪除', 'Delete'],
  'common.read': ['读取', '讀取', 'Read'],
  'common.start': ['开始', '開始', 'Start'],
  'common.save': ['保存', '儲存', 'Save'],
  'common.standard': ['标准', '標準', 'Standard'],
  'common.large': ['大', '大', 'Large'],
  'common.yes': ['是', '是', 'Yes'],
  'common.no': ['否', '否', 'No'],
  'orientation.title': ['请旋转至横屏', '請旋轉至橫屏', 'Please rotate to landscape'],
  'orientation.body': [
    '横屏能保留完整场景和触控区域。旋转后请点击进入全屏。',
    '橫屏能保留完整場景和觸控區域。旋轉後請點擊進入全螢幕。',
    'Landscape keeps the full scene and touch controls visible. After rotating, tap to enter fullscreen.',
  ],
  'fullscreen.eyebrow': ['记忆的缝隙', '記憶的縫隙', 'THE ERASURE OF ME'],
  'fullscreen.title': [
    '准备好进入这段记忆了吗？',
    '準備好進入這段記憶了嗎？',
    'Ready to enter this memory?',
  ],
  'fullscreen.body': [
    '我们会尝试进入全屏，让场景和触控区域更完整。',
    '我們會嘗試進入全螢幕，讓場景和觸控區域更完整。',
    'We will try to enter fullscreen so the scene and touch controls have more room.',
  ],
  'fullscreen.start': ['开始体验', '開始體驗', 'Start experience'],
  'fullscreen.tap': ['点击任意处开始', '點擊任意處開始', 'Tap anywhere to begin'],
  'fullscreen.notice': [
    '无法进入全屏，已继续横屏模式。你可以在设置中再次尝试。',
    '無法進入全螢幕，已繼續橫屏模式。你可以在設定中再次嘗試。',
    'Fullscreen is unavailable, so the game will continue in landscape mode. You can try again in Settings.',
  ],
  'fullscreen.setting.title': ['全屏显示', '全螢幕顯示', 'Fullscreen display'],
  'fullscreen.setting.active': [
    '当前已减少浏览器栏遮挡',
    '目前已減少瀏覽器列遮擋',
    'Browser bars are currently reduced',
  ],
  'fullscreen.setting.inactive': [
    '减少浏览器栏遮挡',
    '減少瀏覽器列遮擋',
    'Reduce browser-bar obstruction',
  ],
  'fullscreen.exit': ['退出全屏', '退出全螢幕', 'Exit fullscreen'],
  'fullscreen.enter': ['进入全屏', '進入全螢幕', 'Enter fullscreen'],
  'save.failed': [
    '无法写入本地存档，请检查浏览器存储空间与隐私设置。',
    '無法寫入本機儲存，請檢查瀏覽器儲存空間與私隱設定。',
    'Unable to write the local save. Check browser storage and privacy settings.',
  ],
  'save.slot': ['记忆片段 {slot}', '記憶片段 {slot}', 'Memory fragment {slot}'],
  'save.slot.read_failed': [
    '记忆片段 {slot} 无法读取。',
    '記憶片段 {slot} 無法讀取。',
    'Memory fragment {slot} could not be read.',
  ],
  'save.slot.empty': ['空白的记忆', '空白的記憶', 'Blank memory'],
  'save.slot.invalid': [
    '这个片段无法读取。删除后可重新开始，其他片段不受影响。',
    '這個片段無法讀取。刪除後可重新開始，其他片段不受影響。',
    'This fragment cannot be read. Delete it to start again; other fragments are unaffected.',
  ],
  'save.time_unknown': ['时间未知', '時間未知', 'Time unknown'],
  'save.overwrite_confirm': [
    '要覆盖「{label}」并从头开始吗？',
    '要覆寫「{label}」並從頭開始嗎？',
    'Overwrite “{label}” and start over?',
  ],
  'save.empty_confirm': [
    '要从这个空白的记忆片段开始吗？',
    '要從這個空白的記憶片段開始嗎？',
    'Start from this blank memory fragment?',
  ],
  'save.overwrite_body': [
    '这个记忆片段中已有的进度将被新的故事替代。',
    '這個記憶片段中已有的進度將被新的故事取代。',
    'The progress in this memory fragment will be replaced by the new story.',
  ],
  'save.overwrite_start': ['覆盖并开始', '覆寫並開始', 'Overwrite and start'],
  'save.delete_confirm': [
    '让「{label}」重新留白吗？',
    '讓「{label}」重新留白嗎？',
    'Make “{label}” blank again?',
  ],
  'save.delete_body': [
    '这段游戏进度将被永久删除，全局无障碍与音量设置会保留。',
    '這段遊戲進度將被永久刪除，全域無障礙與音量設定會保留。',
    'This game progress will be permanently deleted. Global accessibility and volume settings will remain.',
  ],
  'save.delete_done': [
    '记忆片段 {slot} 已重新留白',
    '記憶片段 {slot} 已重新留白',
    'Memory fragment {slot} is blank again',
  ],
  'save.delete_failed': [
    '无法删除记忆片段 {slot}，请检查浏览器存储权限。',
    '無法刪除記憶片段 {slot}，請檢查瀏覽器儲存權限。',
    'Unable to delete memory fragment {slot}. Check browser storage permissions.',
  ],
  'save.delete_action': ['确认删除', '確認刪除', 'Confirm deletion'],
  'save.clear.title': ['本地数据', '本機資料', 'Local data'],
  'save.clear.body': [
    '清除后将删除本机上的全部记忆片段和设置，且无法恢复。',
    '清除後將刪除本機上的全部記憶片段和設定，而且無法復原。',
    'Clearing removes all memory fragments and settings from this device and cannot be undone.',
  ],
  'save.clear.request': ['清除本地数据', '清除本機資料', 'Clear local data'],
  'save.clear.confirm': ['确认清除本地数据', '確認清除本機資料', 'Confirm clear local data'],
  'save.cleared': ['已清除全部本地数据', '已清除全部本機資料', 'All local data cleared'],
  'save.clear_failed': [
    '无法清除全部本地数据，请检查浏览器存储权限。',
    '無法清除全部本機資料，請檢查瀏覽器儲存權限。',
    'Unable to clear all local data. Check browser storage permissions.',
  ],
  'title.eyebrow': [
    '一段关于记忆、尊严与陪伴的故事',
    '一段關於記憶、尊嚴與陪伴的故事',
    'A story about memory, dignity, and companionship',
  ],
  'title.name': ['记忆的缝隙', '記憶的縫隙', '记忆的缝隙'],
  'title.content_warning.title': ['内容提示', '內容提示', 'Content note'],
  'title.content_warning.body': [
    '本作涉及认知衰退、迷路与家庭照护。许志远是虚构人物，他的经历不代表所有阿尔茨海默病患者。你可以随时暂停、退出或启用低扰动模式。',
    '本作涉及認知衰退、迷路與家庭照護。許志遠是虛構人物，他的經歷不代表所有阿茲海默症患者。你可以隨時暫停、退出或啟用低刺激模式。',
    'This game includes cognitive decline, getting lost, and family caregiving. Xu Zhiyuan is fictional; his experience does not represent every person with Alzheimer’s disease. You can pause, exit, or enable Low Stimulation mode at any time.',
  ],
  'menu.aria': ['主菜单', '主選單', 'Main menu'],
  'menu.continue': ['继续游戏', '繼續遊戲', 'Continue game'],
  'menu.continue.empty': [
    '还没有可以继续的记忆',
    '還沒有可以繼續的記憶',
    'No memory is available to continue',
  ],
  'menu.continue.description': [
    '记忆片段 {slot} · {chapter}',
    '記憶片段 {slot} · {chapter}',
    'Memory fragment {slot} · {chapter}',
  ],
  'menu.start': ['开始游戏', '開始遊戲', 'Start game'],
  'menu.start.description': [
    '选择体验方式，从记忆的起点开始',
    '選擇體驗方式，從記憶的起點開始',
    'Choose an experience mode and begin at the memory’s starting point',
  ],
  'menu.memories': ['读取记忆', '讀取記憶', 'Read memories'],
  'menu.memories.description': [
    '查看、读取或整理三个记忆片段',
    '查看、讀取或整理三個記憶片段',
    'View, read, or organise three memory fragments',
  ],
  'menu.settings': ['设置', '設定', 'Settings'],
  'menu.settings.description': [
    '声音、字幕与无障碍选项',
    '聲音、字幕與無障礙選項',
    'Sound, subtitles, and accessibility options',
  ],
  controls: [
    '纯键盘可完成 · WASD / 方向键移动 · E 交互 · Esc 暂停',
    '純鍵盤可完成 · WASD / 方向鍵移動 · E 互動 · Esc 暫停',
    'Playable with keyboard only · WASD / arrow keys move · E interacts · Esc pauses',
  ],
  'controls.observe': ['静静留意', '靜靜留意', 'Quietly notice'],
  'start.eyebrow': ['开始游戏', '開始遊戲', 'Start game'],
  'start.mode.title': ['选择体验方式', '選擇體驗方式', 'Choose an experience mode'],
  'start.mode.body': [
    '体验方式可以在暂停菜单中随时调整，不会重置进度。',
    '體驗方式可以在暫停選單中隨時調整，不會重設進度。',
    'You can change the experience mode in the pause menu without resetting progress.',
  ],
  'mode.standard': ['标准模式', '標準模式', 'Standard mode'],
  'mode.standard.description': [
    '固定、可学习的方向错位与完整退化表现',
    '固定、可學習的方向錯位與完整退化表現',
    'Fixed, learnable direction shift with the full degradation presentation',
  ],
  'mode.low_stimulation': ['低扰动模式', '低刺激模式', 'Low Stimulation mode'],
  'mode.low_stimulation.description': [
    '保留标准方向，降低模糊、漂移和动态',
    '保留標準方向，降低模糊、漂移與動態',
    'Keeps standard directions while reducing blur, drift, and motion',
  ],
  'start.memories.eyebrow': ['开始游戏 · {mode}', '開始遊戲 · {mode}', 'Start game · {mode}'],
  'start.memories.title': ['选择一个记忆片段', '選擇一個記憶片段', 'Choose a memory fragment'],
  'start.memories.body': [
    '故事会自动保存在选中的片段中。',
    '故事會自動儲存在選取的片段中。',
    'The story will be saved automatically in the selected fragment.',
  ],
  'memories.eyebrow': ['读取记忆', '讀取記憶', 'Read memories'],
  'memories.title': ['记忆片段', '記憶片段', 'Memory fragments'],
  'memories.body': [
    '每个片段保存一段独立的游戏进度。',
    '每個片段儲存一段獨立的遊戲進度。',
    'Each fragment stores an independent game progress.',
  ],
  'settings.eyebrow': ['设置', '設定', 'Settings'],
  'settings.title': ['声音与无障碍', '聲音與無障礙', 'Sound and accessibility'],
  'settings.sound': ['声音', '聲音', 'Sound'],
  'settings.accessibility': ['显示与操作', '顯示與操作', 'Display and controls'],
  'settings.muted': [
    '静音（所有声音线索都有视觉替代）',
    '靜音（所有聲音線索都有視覺替代）',
    'Mute (all sound cues have visual alternatives)',
  ],
  'settings.reduced_motion': ['减少动态效果', '減少動態效果', 'Reduce motion'],
  'settings.high_contrast': ['高对比度', '高對比度', 'High contrast'],
  'settings.subtitles': ['字幕', '字幕', 'Subtitles'],
  'settings.font_size': ['文字大小', '文字大小', 'Text size'],
  'settings.hold_mode': ['牵手操作', '牽手操作', 'Hand-holding control'],
  'settings.hold.long': ['长按 1.5 秒', '長按 1.5 秒', 'Hold for 1.5 seconds'],
  'settings.hold.short': ['短按 0.6 秒', '短按 0.6 秒', 'Short press for 0.6 seconds'],
  'settings.hold.single': ['单次确认', '單次確認', 'Single confirmation'],
  'settings.mode': ['体验模式', '體驗模式', 'Experience mode'],
  'settings.language': ['语言', '語言', 'Language'],
  'settings.language.system': ['跟随系统', '跟隨系統', 'Follow system'],
  'settings.language.simplified': ['简体中文', '簡體中文', 'Simplified Chinese'],
  'settings.language.traditional': ['繁体中文', '繁體中文', 'Traditional Chinese'],
  'settings.language.english': ['英文', '英文', 'English'],
  'settings.volume_mixer': ['音量混音', '音量混音', 'Volume mixer'],
  'settings.audio.music': ['音乐', '音樂', 'Music'],
  'settings.audio.ambience': ['环境声', '環境聲', 'Ambience'],
  'settings.audio.voice': ['对白与哼唱', '對白與哼唱', 'Dialogue and humming'],
  'settings.audio.sfx': ['界面与提示音', '介面與提示音', 'Interface and cue sounds'],
  'settings.return_title': ['返回标题', '返回標題', 'Return to title'],
  'hud.objective': ['当前目标', '目前目標', 'Current objective'],
  'hud.status': ['当前信息状态', '目前資訊狀態', 'Current information state'],
  'hud.tools': ['游戏工具', '遊戲工具', 'Game tools'],
  'hud.inventory': ['背包', '背包', 'Inventory'],
  'hud.journal': ['日记', '日記', 'Journal'],
  'hud.map': ['地图', '地圖', 'Map'],
  'hud.close_message': ['关闭提示', '關閉提示', 'Dismiss message'],
  'hud.hold_warmth': ['掌心逐渐变暖', '掌心逐漸變暖', 'Palm growing warm'],
  'panel.inventory.title': ['背包', '背包', 'Inventory'],
  'panel.inventory.body': [
    '名字会变化，物件的形状和纹理不会。',
    '名字會變化，物件的形狀和紋理不會。',
    'Names may change; the shapes and textures of objects will not.',
  ],
  'panel.inventory.empty': [
    '还没有收起任何物件。',
    '還沒有收起任何物件。',
    'No objects have been gathered yet.',
  ],
  'panel.journal.title': ['秀兰的日记', '秀蘭的日記', 'Xiulan’s journal'],
  'panel.journal.empty': [
    '红线书签还停在封面。',
    '紅線書籤還停在封面。',
    'The red thread bookmark is still on the cover.',
  ],
  'panel.map.eyebrow': ['当前空间', '目前空間', 'Current space'],
  'panel.map.legend': ['地图标记', '地圖標記', 'Map markers'],
  'panel.map.full_status': [
    '道路、房间名称和地标仍然清楚。',
    '道路、房間名稱與地標仍然清楚。',
    'Roads, room names, and landmarks are still clear.',
  ],
  'panel.map.washed_status': [
    '水渍遮住了未停留区域。当前位置、已到达的站牌、红伞和钟声方向仍然清楚。',
    '水漬遮住了未停留區域。目前位置、已到達的站牌、紅傘與鐘聲方向仍然清楚。',
    'Water stains cover places you have not visited. Your position, reached stations, the red umbrella, and the bell direction remain clear.',
  ],
  'panel.map.player': [
    '{title}，你在地图上标为蓝色圆点',
    '{title}，你在地圖上標為藍色圓點',
    '{title}; you are marked by the blue dot',
  ],
  'panel.map.reached': ['已到达', '已到達', 'Reached'],
  'panel.photo.kicker': [
    '共同生活 · 相册页 03',
    '共同生活 · 相簿頁 03',
    'Shared life · Album page 03',
  ],
  'panel.photo.title': ['把照片放回时间里', '把相片放回時間裡', 'Put the photos back in time'],
  'panel.photo.body': [
    '沿着年份、家具的新旧和桂花的高度，把三段普通日子接在一起。',
    '沿著年份、家具的新舊與桂花的高度，把三段普通日子接在一起。',
    'Use the years, the age of the furniture, and the height of the osmanthus to connect three ordinary days.',
  ],
  'panel.photo.position': ['第 {position} 张', '第 {position} 張', 'Photo {position}'],
  'panel.photo.position.prefix': ['第', '第', ''],
  'panel.photo.position.suffix': ['张', '張', ''],
  'panel.photo.adjust': [
    '调整 {label} 的位置',
    '調整 {label} 的位置',
    'Adjust the position of {label}',
  ],
  'panel.photo.move_earlier': ['上移 {label}', '上移 {label}', 'Move {label} earlier'],
  'panel.photo.move_later': ['下移 {label}', '下移 {label}', 'Move {label} later'],
  'panel.photo.earlier': ['早一些', '早一些', 'Earlier'],
  'panel.photo.later': ['晚一些', '晚一些', 'Later'],
  'panel.photo.timeline': ['从最早到最近', '由最早到最近', 'Oldest to newest'],
  'panel.photo.confirm': ['确认这条时间线', '確認這條時間線', 'Confirm this timeline'],
  'panel.photo.confirm_aria': ['确认顺序', '確認順序', 'Confirm photo order'],
  'pause.title': ['暂停', '暫停', 'Pause'],
  'pause.settings': ['设置与无障碍', '設定與無障礙', 'Settings and accessibility'],
  'pause.local_data': ['本地数据', '本機資料', 'Local data'],
  'dialogue.continue': ['继续对白', '繼續對白', 'Continue dialogue'],
  'dialogue.keyboard_hint': [
    '点击任意位置，或按 E / Enter / 空格继续',
    '點擊任意位置，或按 E / Enter / 空格繼續',
    'Click anywhere, or press E / Enter / Space to continue',
  ],
  'dialogue.touch_hint': ['点击任意位置继续', '點擊任意位置繼續', 'Tap anywhere to continue'],
  'd3.standard.title': [
    '方向与脚步的关系转过了一圈',
    '方向與腳步的關係轉過了一圈',
    'The relationship between direction and footsteps has turned around',
  ],
  'd3.standard.body': [
    '接下来，按“上”会向世界的右侧移动。这个映射全章固定，不会随机变化。',
    '接下來，按「上」會向世界的右側移動。這個映射全章固定，不會隨機變化。',
    'From now on, pressing “up” moves toward the world’s right. This mapping stays fixed for the chapter and never changes randomly.',
  ],
  'd3.low.title': [
    '旧按键提示变得不可靠',
    '舊按鍵提示變得不可靠',
    'The old key prompts have become unreliable',
  ],
  'd3.low.body': [
    '实际移动仍保持标准方向。地面箭头、伞柄和窗帘会继续告诉你世界方向。',
    '實際移動仍保持標準方向。地面箭頭、傘柄與窗簾會繼續告訴你世界方向。',
    'Movement remains standard. Ground arrows, umbrella handles, and curtains will still show the world’s direction.',
  ],
  'd3.common.body': [
    '暂停、退出和设置永远保持标准操作。',
    '暫停、退出與設定永遠保持標準操作。',
    'Pause, exit, and settings always use standard controls.',
  ],
  'd3.ready': ['我准备好了', '我準備好了', 'I’m ready'],
  'guide.eyebrow': [
    '故事结束后，留下一点可以带走的东西',
    '故事結束後，留下一點可以帶走的東西',
    'After the story, something to carry with you',
  ],
  'guide.title': [
    '早期表现与就医陪伴指南',
    '早期表現與就醫陪伴指南',
    'A guide to early changes and seeking support',
  ],
  'guide.disclaimer': [
    '本页用于一般健康科普，不能替代专业筛查、诊断或治疗。如果你发现自己或家人的认知、情绪或日常能力持续发生变化并影响生活，请记录具体情况，并向正规医疗机构的相关专业人员咨询。',
    '本頁用於一般健康科普，不能取代專業篩查、診斷或治療。如果你發現自己或家人的認知、情緒或日常能力持續變化並影響生活，請記錄具體情況，並向正規醫療機構的相關專業人員諮詢。',
    'This page offers general health information and cannot replace professional screening, diagnosis, or treatment. If changes in cognition, mood, or daily abilities persist and affect life, record what you notice and consult an appropriate professional at a recognised medical service.',
  ],
  'guide.signs.title': ['值得留意的持续变化', '值得留意的持續變化', 'Changes worth noticing'],
  'guide.signs.1': [
    '比过去更频繁地忘记近期事件，或反复询问同一件事',
    '比過去更頻繁地忘記近期事件，或反覆詢問同一件事',
    'Forgetting recent events more often than before, or asking the same question repeatedly',
  ],
  'guide.signs.2': [
    '经常放错物品，并且难以沿原路寻找',
    '經常放錯物品，而且難以沿原路尋找',
    'Often putting objects in unusual places and struggling to retrace the route',
  ],
  'guide.signs.3': [
    '在熟悉地点迷路，或混淆时间、地点',
    '在熟悉地點迷路，或混淆時間、地點',
    'Getting lost in familiar places, or confusing time and place',
  ],
  'guide.signs.4': [
    '完成熟悉任务、解决问题或作出决定变得困难',
    '完成熟悉任務、解決問題或作出決定變得困難',
    'Finding familiar tasks, problem-solving, or decisions more difficult',
  ],
  'guide.signs.5': [
    '跟随对话、理解表达或寻找词语变得困难',
    '跟隨對話、理解表達或尋找詞語變得困難',
    'Finding conversation, understanding, or word-finding more difficult',
  ],
  'guide.signs.6': [
    '视觉空间判断、情绪、行为或社交状态持续改变',
    '視覺空間判斷、情緒、行為或社交狀態持續改變',
    'Persistent changes in visual-spatial judgement, mood, behaviour, or social interaction',
  ],
  'guide.signs.body': [
    '这些表现可能有多种原因，不能凭单一表现自行判断疾病。',
    '這些表現可能有多種原因，不能單憑一項表現自行判斷疾病。',
    'These changes can have many causes; a single sign cannot determine a disease.',
  ],
  'guide.actions.title': ['可以怎样行动', '可以怎樣行動', 'What you can do'],
  'guide.actions.1': [
    '记录变化出现的时间、频率、场景和对生活的影响。',
    '記錄變化出現的時間、頻率、場景與對生活的影響。',
    'Record when changes happen, how often, in what situations, and how they affect daily life.',
  ],
  'guide.actions.2': [
    '与本人平静沟通，避免测试、指责或争辩。',
    '與本人平靜溝通，避免測試、指責或爭辯。',
    'Communicate calmly and avoid testing, blaming, or arguing.',
  ],
  'guide.actions.3': [
    '预约正规医疗机构评估，携带病史、用药信息和观察记录。',
    '預約正規醫療機構評估，帶備病史、用藥資訊與觀察記錄。',
    'Arrange an assessment at a recognised medical service and bring medical history, medication information, and notes.',
  ],
  'guide.actions.4': [
    '用稳定日程、清晰标记、充足照明改善日常安全。',
    '用穩定日程、清晰標記與充足照明改善日常安全。',
    'Use stable routines, clear labels, and good lighting to support everyday safety.',
  ],
  'guide.actions.5': [
    '鼓励本人继续参与力所能及的熟悉活动与社会交往。',
    '鼓勵本人繼續參與力所能及的熟悉活動與社交往來。',
    'Encourage familiar activities and social connection within the person’s abilities.',
  ],
  'guide.actions.6': [
    '照护者也需要休息，并可以向家人、社区和专业人员求助。',
    '照護者也需要休息，並可以向家人、社區與專業人士求助。',
    'Caregivers need rest too, and can seek help from family, community, and professionals.',
  ],
  'guide.game.title': ['关于游戏中的谜题', '關於遊戲中的謎題', 'About the puzzles in this game'],
  'guide.game.body': [
    '照片排序、数字连接、颜色和形状辨识只用于叙事体验，不是医学筛查，也不能产生任何认知健康结论。',
    '相片排序、數字連接、顏色與形狀辨識只用於敘事體驗，不是醫學篩查，也不能產生任何認知健康結論。',
    'Photo ordering, number connections, and colour or shape recognition are only narrative experiences. They are not medical screening and cannot produce conclusions about cognitive health.',
  ],
  'guide.sources.title': ['资料来源', '資料來源', 'Sources'],
  'guide.sources.checked': [
    '来源最近核验：2026-06-23。正式发布前仍需专业审核。',
    '來源最近核驗：2026-06-23。正式發布前仍需專業審核。',
    'Sources last checked: 2026-06-23. Professional review is still required before release.',
  ],
  'guide.credits.title': ['制作与致谢', '製作與鳴謝', 'Credits and thanks'],
  'guide.credits.body': [
    '本作由项目团队原创制作，使用 Phaser、TypeScript、Vite 与 Tiled。生成式图像的来源和处理记录见仓库资产台账。',
    '本作由項目團隊原創製作，使用 Phaser、TypeScript、Vite 與 Tiled。生成式圖像的來源與處理記錄見倉庫資產台帳。',
    'This work was created by the project team using Phaser, TypeScript, Vite, and Tiled. Sources and processing records for generated images are listed in the repository asset register.',
  ],
  'guide.credits.body2': [
    '感谢公开权威资料的维护者；上列机构未参与本作制作，也不代表对本作背书。医学与敏感性专业审核完成后，仅在取得同意时补充公开致谢。',
    '感謝公開權威資料的維護者；上列機構未參與本作製作，也不代表對本作背書。醫學與敏感性專業審核完成後，僅在取得同意時補充公開鳴謝。',
    'Thank you to the maintainers of the public authoritative sources. The organisations listed above did not participate in making this game and do not endorse it. Public thanks for professional medical and sensitivity reviewers will be added only with their consent.',
  ],
  'guide.source.who': [
    '世界卫生组织：Dementia',
    '世界衛生組織：Dementia',
    'World Health Organization: Dementia',
  ],
  'guide.source.cn_action': [
    '应对老年期痴呆国家行动计划（2024—2030年）',
    '應對老年期癡呆國家行動計劃（2024—2030年）',
    'National Action Plan for Addressing Dementia in Older Adults (2024–2030)',
  ],
  'guide.source.cn_explain': [
    '国家行动计划政策解读',
    '國家行動計劃政策解讀',
    'Policy interpretation of the national action plan',
  ],
  'entity.home.bedside_photo': ['床边合影', '床邊合照', 'Photo by the bedside'],
  'entity.home.journal': ['红线日记', '紅線日記', 'Red-thread journal'],
  'entity.home.glasses_case': ['眼镜盒', '眼鏡盒', 'Glasses case'],
  'entity.home.key_bowl': ['蓝色小碗', '藍色小碗', 'Blue bowl'],
  'entity.home.front_door': ['玄关门', '玄關門', 'Entry door'],
  'entity.rain.ticket': ['泡软的旧车票', '泡軟的舊車票', 'Softened old ticket'],
  'entity.rain.stone_2': ['两个圆点', '兩個圓點', 'Two dots'],
  'entity.rain.stone_4': ['四个圆点', '四個圓點', 'Four dots'],
  'entity.rain.stone_5': ['五个圆点', '五個圓點', 'Five dots'],
  'entity.rain.umbrella_sign_a': ['红伞招牌', '紅傘招牌', 'Red umbrella sign'],
  'entity.rain.umbrella_sign_b': ['又一把红伞', '又一把紅傘', 'Another red umbrella'],
  'entity.rain.red_umbrella': [
    '钟表铺前的红伞',
    '鐘錶舖前的紅傘',
    'Red umbrella by the clock shop',
  ],
  'entity.life.album': ['空着三格的相册', '空著三格的相簿', 'Album with three empty spaces'],
  'entity.life.wood_comb': ['有裂缝的条纹物件', '有裂縫的條紋物件', 'Cracked striped object'],
  'entity.life.enamel_cup': ['带桂花圆点的物件', '帶桂花圓點的物件', 'Osmanthus-dotted object'],
  'entity.life.cassette': ['双线圈波纹物件', '雙線圈波紋物件', 'Double-loop wave object'],
  'entity.life.exit': ['延长的走廊', '延長的走廊', 'The extended corridor'],
  'entity.ending.xiulan': ['她把手停在那里', '她把手停在那裡', 'She holds out her hand'],
  'item.home.journal': ['红线日记', '紅線日記', 'Red-thread journal'],
  'item.home.key': ['家门钥匙', '家門鑰匙', 'House key'],
  'item.home.glasses_case': ['眼镜盒', '眼鏡盒', 'Glasses case'],
  'item.rain.ticket': ['旧车票 · 2 → 4 → 5', '舊車票 · 2 → 4 → 5', 'Old ticket · 2 → 4 → 5'],
  'item.photo.1979': ['1979 · 搬家', '1979 · 搬家', '1979 · Moving day'],
  'item.photo.1992': ['1992 · 桂花窗台', '1992 · 桂花窗台', '1992 · Osmanthus windowsill'],
  'item.photo.2001': ['2001 · 银婚', '2001 · 銀婚', '2001 · Silver anniversary'],
  'item.photo.move_1979': ['纸箱旁的照片', '紙箱旁的相片', 'Photo by the moving boxes'],
  'item.photo.osmanthus_1992': ['桂花窗台照片', '桂花窗台相片', 'Osmanthus windowsill photo'],
  'item.photo.anniversary_2001': ['银婚照片', '銀婚相片', 'Silver anniversary photo'],
  'item.life.wood_comb': ['木梳 · 条纹', '木梳 · 條紋', 'Wooden comb · stripes'],
  'item.life.enamel_cup': ['搪瓷杯 · 圆点', '搪瓷杯 · 圓點', 'Enamel cup · dots'],
  'item.life.cassette': ['录音带 · 波纹', '錄音帶 · 波紋', 'Cassette · waves'],
  'item.life.wood_comb.d2': ['木＿ · 条纹', '木＿ · 條紋', 'Wooden ＿ · stripes'],
  'item.life.enamel_cup.d2': ['＿瓷＿ · 圆点', '＿瓷＿ · 圓點', '＿ware ＿ · dots'],
  'item.life.cassette.d2': ['〰 〰 〰 · 波纹', '〰 〰 〰 · 波紋', '〰 〰 〰 · waves'],
  'slot.life.dresser': ['镜台 · 条纹槽', '鏡台 · 條紋槽', 'Dresser · striped slot'],
  'slot.life.windowsill': ['窗台 · 圆点槽', '窗台 · 圓點槽', 'Windowsill · dotted slot'],
  'slot.life.radio': ['收音机 · 波纹槽', '收音機 · 波紋槽', 'Radio · wavy slot'],
  'route.up': ['向上的出口', '向上的出口', 'Exit above'],
  'route.right': ['向右的出口', '向右的出口', 'Exit to the right'],
  'route.down': ['向下的出口', '向下的出口', 'Exit below'],
  'route.left': ['向左的出口', '向左的出口', 'Exit to the left'],
  'chapter.home.title': ['第一章 · 清晨的家', '第一章 · 清晨的家', 'Chapter 1 · A morning at home'],
  'chapter.home.subtitle': [
    '熟悉的东西，都在它们原来的位置。',
    '熟悉的東西，都在它們原來的位置。',
    'Familiar things are where they have always been.',
  ],
  'chapter.rain.title': [
    '第二章 · 雨中的初遇',
    '第二章 · 雨中的初遇',
    'Chapter 2 · Meeting in the rain',
  ],
  'chapter.rain.subtitle': [
    '有些路名看不清了。钟声还在。',
    '有些路名看不清了。鐘聲還在。',
    'Some street names are hard to read. The bell is still there.',
  ],
  'chapter.life.title': ['第三章 · 共同生活', '第三章 · 共同生活', 'Chapter 3 · A shared life'],
  'chapter.life.subtitle': [
    '名字有些远。样子和触感还在。',
    '名字有些遠。樣子與觸感還在。',
    'Names feel distant. Shapes and textures remain.',
  ],
  'chapter.return.title': ['第四章 · 回家的路', '第四章 · 回家的路', 'Chapter 4 · The way home'],
  'chapter.return.subtitle': [
    '方向变了。地上的箭头还在。',
    '方向變了。地上的箭頭還在。',
    'The directions changed. The arrows on the ground remain.',
  ],
  'chapter.ending.title': [
    '尾声 · 面还是热的',
    '尾聲 · 麵還是熱的',
    'Epilogue · The noodles are still warm',
  ],
  'chapter.ending.subtitle': ['', '', ''],
  'objective.home.start': [
    '找到钥匙和秀兰留下的日记',
    '找到鑰匙和秀蘭留下的日記',
    'Find the key and Xiulan’s journal',
  ],
  'objective.rain.start': [
    '按 2 → 4 → 5 踩亮石板，再跟随红伞',
    '按 2 → 4 → 5 踩亮石板，再跟隨紅傘',
    'Light the stones 2 → 4 → 5, then follow the red umbrella',
  ],
  'objective.life.start': [
    '整理照片，并让三件生活物品回到原处',
    '整理相片，並讓三件生活物品回到原處',
    'Arrange the photos and return three household objects',
  ],
  'objective.return.start': [
    '理解新的方向，沿着仍可靠的线索回家',
    '理解新的方向，沿著仍可靠的線索回家',
    'Learn the new directions and follow the reliable clues home',
  ],
  'objective.ending.start': ['走近秀兰', '走近秀蘭', 'Approach Xiulan'],
  'objective.life.photos': [
    '找齐三张照片，在相册中按时间排序',
    '找齊三張相片，在相簿中按時間排序',
    'Find all three photos and order them in the album',
  ],
  'objective.life.objects': [
    '让生活物品回到原处（{count}/3）',
    '讓生活物品回到原處（{count}/3）',
    'Return the household objects ({count}/3)',
  ],
  'objective.life.corridor': [
    '走进房间上方延长的走廊',
    '走進房間上方延長的走廊',
    'Enter the extended corridor above the room',
  ],
  'objective.return.door': [
    '与上方的家门交互',
    '與上方的家門互動',
    'Interact with the house door above',
  ],
  'message.photo.order.done': [
    '三个年份安静地排在了一起。',
    '三個年份安靜地排在了一起。',
    'The three years sit quietly together.',
  ],
  'message.photo.order.wrong': [
    '这个顺序似乎接不上。其他照片没有移动。',
    '這個順序似乎接不上。其他相片沒有移動。',
    'This order does not seem to connect. The other photos did not move.',
  ],
  'message.d1.started': [
    '有些路名看不清了。钟声还在。',
    '有些路名看不清了。鐘聲還在。',
    'Some street names are hard to read. The bell is still there.',
  ],
  'system.degradation.d1': [
    '有些路名看不清了。钟声还在。',
    '有些路名看不清了。鐘聲還在。',
    'Some street names are hard to read. The bell is still there.',
  ],
  'degraded.map.unknown_place': ['……', '……', '…'],
  'message.d3.standard': [
    '方向变了。地上的箭头还在。',
    '方向變了。地上的箭頭還在。',
    'The directions changed. The arrows on the ground remain.',
  ],
  'message.d3.low': [
    '提示变得不可靠。地上的箭头还在。',
    '提示變得不可靠。地上的箭頭還在。',
    'The prompts have become unreliable. The arrows on the ground remain.',
  ],
  'message.hold.long': [
    '按住确认键，握住她的手',
    '按住確認鍵，牽住她的手',
    'Hold the confirm key to take her hand',
  ],
  'message.hold.short': [
    '按确认键，握住她的手',
    '按確認鍵，牽住她的手',
    'Press the confirm key to take her hand',
  ],
  'dialogue.opening.question': [
    '钥匙……今天要出门吗？',
    '鑰匙……今天要出門嗎？',
    'The key… are we going out today?',
  ],
  'dialogue.opening.controls': [
    '使用 WASD 或方向键移动，靠近物件后按 E 交互。',
    '使用 WASD 或方向鍵移動，靠近物件後按 E 互動。',
    'Use WASD or the arrow keys to move. Approach an object and press E to interact.',
  ],
  'dialogue.home.photo.1': [
    '照片里，我们站在一把红伞下面。',
    '相片裡，我們站在一把紅傘下面。',
    'In the photo, we are standing beneath a red umbrella.',
  ],
  'dialogue.home.photo.2': [
    '伞面太小，两个人的肩膀都湿了一半。',
    '傘面太小，兩個人的肩膀都濕了一半。',
    'The umbrella is too small; half of both our shoulders are wet.',
  ],
  'dialogue.home.journal.1': [
    '六月二十二日，晴转雨。',
    '六月二十二日，晴轉雨。',
    'June 22. Clear skies turning to rain.',
  ],
  'dialogue.home.journal.2': [
    '钥匙还在玄关的蓝色小碗里。别急，我去买面，很快回来。——秀兰',
    '鑰匙還在玄關的藍色小碗裡。別急，我去買麵，很快回來。——秀蘭',
    'The key is still in the blue bowl by the entrance. Don’t hurry. I’m going to buy noodles and will be right back. — Xiulan',
  ],
  'dialogue.home.door.1': [
    '钥匙插入门锁。远处响起雷声。',
    '鑰匙插入門鎖。遠處響起雷聲。',
    'The key turns in the lock. Thunder sounds in the distance.',
  ],
  'dialogue.home.door.2': [
    '这场雨……我见过。',
    '這場雨……我見過。',
    'This rain… I have seen it before.',
  ],
  'dialogue.rain.1': [
    '年轻的林秀兰：“你要去车站吗？”',
    '年輕的林秀蘭：「你要去車站嗎？」',
    'Young Xiulan: “Are you going to the station?”',
  ],
  'dialogue.rain.2': ['许志远：“……是。”', '許志遠：「……是。」', 'Zhiyuan: “…Yes.”'],
  'dialogue.rain.3': [
    '林秀兰：“那一起走吧。”',
    '林秀蘭：「那一起走吧。」',
    'Xiulan: “Then let’s go together.”',
  ],
  'dialogue.rain.5': [
    '林秀兰：“伞往你那边一点，别淋着。”',
    '林秀蘭：「傘往你那邊一點，別淋著。」',
    'Xiulan: “I’ll tilt the umbrella your way. Don’t get wet.”',
  ],
  'narration.rain.memory_end': [
    '那天的雨很大。我不记得车开去了哪里，只记得她的半边肩膀湿了。',
    '那天的雨很大。我不記得車開去了哪裡，只記得她的半邊肩膀濕了。',
    'The rain was heavy that day. I do not remember where the train went. I only remember that half her shoulder was wet.',
  ],
  'dialogue.life.move': [
    '一起拆纸箱，床还没装好，两个人先坐在箱子上笑。',
    '一起拆紙箱，床還沒裝好，兩個人先坐在箱子上笑。',
    'We unpacked the boxes together. The bed was not assembled yet, so we sat on the boxes and laughed.',
  ],
  'dialogue.life.osmanthus': [
    '桂花第一次开。她说，明年也一起闻。',
    '桂花第一次開。她說，明年也一起聞。',
    'The osmanthus bloomed for the first time. She said we would smell it together next year too.',
  ],
  'dialogue.life.cassette': [
    '停电的纪念日里，录音带的声音还在。',
    '停電的紀念日裡，錄音帶的聲音還在。',
    'On the anniversary when the power went out, the cassette kept playing.',
  ],
  'dialogue.life.complete.1': [
    '不是照片替我们记住了一生。',
    '不是相片替我們記住了一生。',
    'It was not the photos that remembered a life for us.',
  ],
  'dialogue.life.complete.2': [
    '是这些做过很多次、当时并不觉得特别的小事。',
    '是這些做過很多次、當時並不覺得特別的小事。',
    'It was the small things we did many times without thinking they were special.',
  ],
  'dialogue.ending.1': ['林秀兰：“我回来了。”', '林秀蘭：「我回來了。」', 'Xiulan: “I’m home.”'],
  'dialogue.ending.2': ['许志远：“我……你是……”', '許志遠：「我……你是……」', 'Zhiyuan: “I… you are…”'],
  'dialogue.ending.3': [
    '林秀兰：“我是秀兰。别急，我回来了。”',
    '林秀蘭：「我是秀蘭。別急，我回來了。」',
    'Xiulan: “It’s Xiulan. Don’t hurry. I’m home.”',
  ],
  'dialogue.ending.4': [
    '她把手停在那里，没有催我。',
    '她把手停在那裡，沒有催我。',
    'She held out her hand and did not hurry me.',
  ],
  'dialogue.ending.hand.1': [
    '许志远主动握住她的手：“手是暖的。”',
    '許志遠主動牽住她的手：「手是暖的。」',
    'Zhiyuan took her hand: “Your hand is warm.”',
  ],
  'dialogue.ending.hand.2': [
    '林秀兰：“面也还是热的。”',
    '林秀蘭：「麵也還是熱的。」',
    'Xiulan: “The noodles are still warm too.”',
  ],
  'dialogue.ending.hand.3': [
    '有些名字会远去，爱曾经来过的地方还留着温度。',
    '有些名字會遠去，愛曾經來過的地方還留著溫度。',
    'Some names grow distant. The places love has touched still hold its warmth.',
  ],
  'dialogue.chapter.rain.1': [
    '现实的门锁声变成车站广播的底噪。',
    '現實的門鎖聲變成車站廣播的底噪。',
    'The sound of the real door lock becomes the low hum of a station announcement.',
  ],
  'dialogue.chapter.rain.2': ['这是哪一站？', '這是哪一站？', 'Which station is this?'],
  'dialogue.rain.arrival': [
    '现实门锁声变为车站广播底噪。',
    '現實門鎖聲變為車站廣播底噪。',
    'The real door lock becomes the low hum of a station announcement.',
  ],
  'dialogue.rain.arrival.question': [
    '许志远：“这是哪一站？”',
    '許志遠：「這是哪一站？」',
    'Zhiyuan: “Which station is this?”',
  ],
  'dialogue.chapter.life.1': [
    '这个家……怎么有三扇一样的窗？',
    '這個家……怎麼有三扇一樣的窗？',
    'This home… why are there three identical windows?',
  ],
  'dialogue.chapter.life.2': [
    '名字有些远。样子和触感还在。',
    '名字有些遠。樣子與觸感還在。',
    'Names feel distant. Shapes and textures remain.',
  ],
  'dialogue.chapter.return.1': ['路怎么转了？', '路怎麼轉了？', 'How did the path turn?'],
  'dialogue.chapter.return.2': [
    '先看看地上的箭头。这里不会催你。',
    '先看看地上的箭頭。這裡不會催你。',
    'Look at the arrows on the ground first. Nothing here will hurry you.',
  ],
  'dialogue.chapter.ending.1': [
    '门打开了。熟悉的清晨重新落回房间。',
    '門打開了。熟悉的清晨重新落回房間。',
    'The door opens. A familiar morning settles back into the room.',
  ],
  'message.home.key_found': ['取得：家门钥匙', '取得：家門鑰匙', 'Acquired: house key'],
  'message.home.glasses': [
    '眼镜盒是空的。边角贴着一小块红胶布。',
    '眼鏡盒是空的。邊角貼著一小塊紅膠布。',
    'The glasses case is empty. A small piece of red tape is stuck to one corner.',
  ],
  'message.home.key_hint': [
    '钥匙也许还在玄关的小碗里。',
    '鑰匙也許還在玄關的小碗裡。',
    'The key may still be in the small bowl by the entrance.',
  ],
  'message.home.journal_hint': [
    '桌边的日记也许会有帮助。',
    '桌邊的日記也許會有幫助。',
    'The journal by the table may help.',
  ],
  'message.rain.ticket': [
    '旧车票背面写着：2 → 4 → 5',
    '舊車票背面寫著：2 → 4 → 5',
    'The back of the old ticket says: 2 → 4 → 5',
  ],
  'inspect.rain.ticket': [
    '一张被雨水泡软的旧车票。背面用铅笔写着：2 → 4 → 5。',
    '一張被雨水泡軟的舊車票。背面用鉛筆寫著：2 → 4 → 5。',
    'An old ticket softened by rain. On the back, in pencil: 2 → 4 → 5.',
  ],
  'system.item_pickup.old_ticket': ['取得：旧车票', '取得：舊車票', 'Acquired: old ticket'],
  'inspect.rain.stone_2': [
    '两个圆点，边缘磨得发亮。',
    '兩個圓點，邊緣磨得發亮。',
    'Two dots, their edges polished bright.',
  ],
  'inspect.rain.stone_4': [
    '四个圆点，雨水积在凹槽里。',
    '四個圓點，雨水積在凹槽裡。',
    'Four dots, rain pooling in their grooves.',
  ],
  'inspect.rain.stone_5': [
    '五个圆点。最后一个像是后来补刻的。',
    '五個圓點。最後一個像是後來補刻的。',
    'Five dots. The last one looks like it was carved later.',
  ],
  'message.rain.stone_progress': [
    '石板在雨里亮了一下。',
    '石板在雨裡亮了一下。',
    'A stone glows briefly in the rain.',
  ],
  'system.rain.sequence_progress': [
    '石板在雨里亮了一下。',
    '石板在雨裡亮了一下。',
    'A stone glows briefly in the rain.',
  ],
  'message.rain.stone_done': [
    '三块石板亮起。雨幕深处，一把红伞变得清晰。',
    '三塊石板亮起。雨幕深處，一把紅傘變得清晰。',
    'Three stones light up. A red umbrella becomes clear through the rain.',
  ],
  'message.rain.stone_wrong': [
    '这块石板没有回应。已经亮起的还在。',
    '這塊石板沒有回應。已經亮起的還在。',
    'This stone does not respond. The ones already lit remain.',
  ],
  'system.rain.sequence_soft_miss': [
    '这块石板没有回应。已经亮起的还在。',
    '這塊石板沒有回應。已經亮起的還在。',
    'This stone does not respond. The ones already lit remain.',
  ],
  'message.rain.sign_a': [
    '褪色的伞铺招牌。伞柄朝向小巷。',
    '褪色的傘舖招牌。傘柄朝向小巷。',
    'A faded umbrella-shop sign. The handle points toward the lane.',
  ],
  'message.rain.sign_b': [
    '又一把红伞。雨太大，只有这个颜色没有散开。',
    '又一把紅傘。雨太大，只有這個顏色沒有散開。',
    'Another red umbrella. The rain is so heavy that only this colour stays intact.',
  ],
  'inspect.rain.umbrella_sign_a': [
    '褪色的伞铺招牌。伞柄朝向小巷。',
    '褪色的傘舖招牌。傘柄朝向小巷。',
    'A faded umbrella-shop sign. The handle points toward the lane.',
  ],
  'inspect.rain.umbrella_sign_b': [
    '又一把红伞。雨太大，只有这个颜色没有散开。',
    '又一把紅傘。雨太大，只有這個顏色沒有散開。',
    'Another red umbrella. The rain is so heavy that only this colour stays intact.',
  ],
  'message.rain.sign_wrong': [
    '钟声让积水朝更近的一把红伞发颤。',
    '鐘聲讓積水朝更近的一把紅傘發顫。',
    'The bell makes the puddle tremble toward a nearer red umbrella.',
  ],
  'system.rain.sound_visual': [
    '钟声让积水向一个方向轻轻发颤。',
    '鐘聲讓積水向一個方向輕輕發顫。',
    'The bell makes the puddle tremble in one direction.',
  ],
  'message.rain.umbrella_hint': [
    '雨太密了。旧车票、石板和路边的伞会带我过去。',
    '雨太密了。舊車票、石板與路邊的傘會帶我過去。',
    'The rain is too thick. The old ticket, stones, and umbrellas by the road will guide me.',
  ],
  'message.life.photo_found': ['取得照片：{year}', '取得相片：{year}', 'Acquired photo: {year}'],
  'message.life.comb': [
    '木＿：齿很密，握柄有一道修过的裂缝。',
    '木＿：齒很密，握柄有一道修補過的裂縫。',
    'Wood＿: close teeth, with a repaired crack in the handle.',
  ],
  'message.life.cup': [
    '＿瓷＿：一圈桂花，底部磨出了银色。',
    '＿瓷＿：一圈桂花，底部磨出了銀色。',
    '＿celain＿: a ring of osmanthus, silver worn into the base.',
  ],
  'message.life.cassette': [
    '〰 〰 〰：双线圈里藏着熟悉的旋律。',
    '〰 〰 〰：雙線圈裡藏著熟悉的旋律。',
    '〰 〰 〰: a familiar melody hides in the double waves.',
  ],
  'message.life.album_missing': [
    '相册空着三格。散落在房间里的照片还没有找齐。',
    '相簿空著三格。散落在房間裡的相片還沒有找齊。',
    'The album has three empty spaces. The photos scattered around the room are not all found yet.',
  ],
  'message.life.slot_missing': [
    '这个位置留下了熟悉的纹理，但对应的物件还没找到。',
    '這個位置留下了熟悉的紋理，但對應的物件還沒找到。',
    'This place holds a familiar texture, but its object has not been found.',
  ],
  'message.life.photo_order_first': [
    '也许先把照片的年份排好，会看得更清楚。',
    '也許先把相片的年份排好，會看得更清楚。',
    'Perhaps the years will become clearer if the photos are ordered first.',
  ],
  'message.life.not_complete': [
    '房间还没有收束。照片和三件物品都在等自己的位置。',
    '房間還沒有收束。相片與三件物品都在等自己的位置。',
    'The room is not settled yet. The photos and three objects are still waiting for their places.',
  ],
  'message.return.humming_above': [
    '门后有人在哼歌。它就在上方。',
    '門後有人在哼歌。它就在上方。',
    'Someone is humming behind the door. It is above.',
  ],
  'message.return.door_appeared': [
    '门后有人在哼歌。向上的家门已经出现。',
    '門後有人在哼歌。向上的家門已經出現。',
    'Someone is humming behind the door. The house door above has appeared.',
  ],
  'message.return.junction': [
    '走过路口 {junction}。熟悉的空间重新落在脚下。',
    '走過路口 {junction}。熟悉的空間重新落在腳下。',
    'Junction {junction} passed. Familiar space settles beneath your feet again.',
  ],
  'message.return.step_right': [
    '这一步和仍留下的线索对上了。',
    '這一步和仍留下的線索對上了。',
    'This step matches the clues that remain.',
  ],
  'message.return.loop_one': [
    '我好像又回来了。红伞还在前面。',
    '我好像又回來了。紅傘還在前面。',
    'I seem to be back again. The red umbrella is still ahead.',
  ],
  'message.return.loop_two': [
    '地上有一对浅浅的脚印。有人走过，也有人在等我。',
    '地上有一對淺淺的腳印。有人走過，也有人在等我。',
    'There is a pair of faint footprints. Someone passed by, and someone is waiting for me.',
  ],
  'message.return.loop_three': [
    '先朝伞柄指向的方向走。按键和脚步的关系已经转过一圈。',
    '先朝傘柄指向的方向走。按鍵與腳步的關係已經轉過一圈。',
    'Walk toward the direction of the umbrella handle first. The relationship between keys and footsteps has turned around.',
  ],
  'hint.home.1': [
    '玄关的蓝色小碗反射了一点晨光。',
    '玄關的藍色小碗反射了一點晨光。',
    'The blue bowl by the entrance catches a little morning light.',
  ],
  'hint.home.2': [
    '日记里的红线书签轻轻露出桌沿。',
    '日記裡的紅線書籤輕輕露出桌沿。',
    'The red thread bookmark in the journal peeks over the table edge.',
  ],
  'hint.home.3': [
    '从客厅到玄关，地上浮出一串很淡的脚印。',
    '從客廳到玄關，地上浮出一串很淡的腳印。',
    'A faint line of footprints rises from the living room to the entrance.',
  ],
  'hint.rain.1': [
    '旧车票边缘发亮：2 → 4 → 5。',
    '舊車票邊緣發亮：2 → 4 → 5。',
    'The edge of the old ticket glints: 2 → 4 → 5.',
  ],
  'hint.rain.2': [
    '下一块石板的圆点在积水里映了一下。',
    '下一塊石板的圓點在積水裡映了一下。',
    'The dots on the next stone reflect in the puddle.',
  ],
  'hint.rain.3': [
    '红伞与钟声方向之间，水面连续泛起波纹。',
    '紅傘與鐘聲方向之間，水面連續泛起波紋。',
    'Ripples pass across the water between the red umbrella and the bell.',
  ],
  'hint.life.1': [
    '照片空位显出年代轮廓。',
    '相片空位顯出年代輪廓。',
    'The empty spaces in the album suggest the years.',
  ],
  'hint.life.2': [
    '条纹、圆点和波纹在相应位置轻轻回应。',
    '條紋、圓點與波紋在相應位置輕輕回應。',
    'Stripes, dots, and waves answer softly from their places.',
  ],
  'hint.life.3': [
    '正确位置出现了物件的剪影。',
    '正確位置出現了物件的剪影。',
    'The silhouette of an object appears in the right place.',
  ],
  'hint.return.1': [
    '当前红伞标记更亮了一点。',
    '目前紅傘標記更亮了一點。',
    'The current red umbrella marker is a little brighter.',
  ],
  'hint.return.2': [
    '正确世界方向出现一对淡脚印。',
    '正確世界方向出現一對淡腳印。',
    'A pair of faint footprints appears in the correct world direction.',
  ],
  'hint.return.3': [
    '先朝伞柄指向的方向走。按键和脚步的关系已经转过一圈。',
    '先朝傘柄指向的方向走。按鍵與腳步的關係已經轉過一圈。',
    'Walk toward the direction of the umbrella handle first. The relationship between keys and footsteps has turned around.',
  ],
  'legacy.unknown': ['{source}', '（未翻譯文本）{source}', '[Untranslated text] {source}'],
  'legacy.unknown_chapter': ['未知章节', '未知章節', 'Unknown chapter'],
  'legacy.photo.unknown': ['未识别的照片', '未識別的相片', 'Unidentified photo'],
  'legacy.photo.clue_unknown': [
    '暂时无法辨认这张照片的线索。',
    '暫時無法辨認這張相片的線索。',
    'The clue in this photo cannot be identified yet.',
  ],
  'legacy.photo.moment_unknown': ['未命名片段', '未命名片段', 'Unnamed fragment'],
  'legacy.photo.caption.1979': ['1979 · 搬家纸箱', '1979 · 搬家紙箱', '1979 · Moving boxes'],
  'legacy.photo.caption.1992': [
    '1992 · 桂花窗台',
    '1992 · 桂花窗台',
    '1992 · Osmanthus windowsill',
  ],
  'legacy.photo.caption.2001': [
    '2001 · 银婚蛋糕',
    '2001 · 銀婚蛋糕',
    '2001 · Silver anniversary cake',
  ],
  'legacy.photo.clue.1979': [
    '年份写在尚未拆开的纸箱角落。',
    '年份寫在尚未拆開的紙箱角落。',
    'The year is written on the corner of an unopened moving box.',
  ],
  'legacy.photo.clue.1992': [
    '年份写在孩子的身高刻度旁。',
    '年份寫在孩子的身高刻度旁。',
    'The year is written beside the child’s height marks.',
  ],
  'legacy.photo.clue.2001': [
    '年份写在银婚蛋糕的小牌上。',
    '年份寫在銀婚蛋糕的小牌上。',
    'The year is written on the small sign on the anniversary cake.',
  ],
  'legacy.photo.alt.1979': [
    '尚未拆完的搬家纸箱、空房和靠在一旁的红伞',
    '尚未拆完的搬家紙箱、空房與靠在一旁的紅傘',
    'Unopened moving boxes, an empty room, and a red umbrella leaning nearby',
  ],
  'legacy.photo.alt.1992': [
    '桂花窗台、孩子的身高刻度和一只旧搪瓷杯',
    '桂花窗台、孩子的身高刻度與一隻舊搪瓷杯',
    'An osmanthus windowsill, a child’s height marks, and an old enamel cup',
  ],
  'legacy.photo.alt.2001': [
    '银婚蛋糕、银色双环与已经一起生活多年的两个人',
    '銀婚蛋糕、銀色雙環與已經一起生活多年的兩個人',
    'A silver anniversary cake, silver rings, and two people who have shared many years',
  ],
  'memory.rain.label': ['雨中的初遇记忆', '雨中的初遇記憶', 'Memory of meeting in the rain'],
  'memory.rain.alt': [
    '年轻的秀兰在旧车站把修补过的红伞倾向淋雨的志远',
    '年輕的秀蘭在舊車站把修補過的紅傘傾向淋雨的志遠',
    'Young Xiulan tilts a repaired red umbrella toward Zhiyuan in the rain at the old station',
  ],
  'memory.rain.umbrella.title': [
    '雨伞下的半边晴天',
    '雨傘下的半邊晴天',
    'Half a Clear Sky Beneath the Umbrella',
  ],
  'memory.rain.umbrella.body': [
    '她把伞偏过来，自己淋湿了半边肩膀。',
    '她把傘偏過來，自己淋濕了半邊肩膀。',
    'She tilted the umbrella toward him and let half her own shoulder get wet.',
  ],
  'memory.life.move.label': [
    '搬进新家的记忆',
    '搬進新家的記憶',
    'Memory of moving into a new home',
  ],
  'memory.life.move.alt': [
    '年轻的志远和秀兰坐在搬家纸箱上，未装好的床架和旧红伞留在一旁',
    '年輕的志遠和秀蘭坐在搬家紙箱上，未裝好的床架與舊紅傘留在一旁',
    'Young Zhiyuan and Xiulan sit on moving boxes while an unassembled bed frame and an old red umbrella rest nearby',
  ],
  'memory.life.osmanthus.label': [
    '桂花第一次开放的记忆',
    '桂花第一次開放的記憶',
    'Memory of the first osmanthus bloom',
  ],
  'memory.life.osmanthus.alt': [
    '中年的秀兰擦拭桂花窗台，志远拿着带桂花纹样的搪瓷杯站在一旁',
    '中年的秀蘭擦拭桂花窗台，志遠拿著帶桂花紋樣的搪瓷杯站在一旁',
    'Middle-aged Xiulan wipes the osmanthus windowsill while Zhiyuan holds an enamel cup with an osmanthus pattern',
  ],
  'memory.life.cassette.label': [
    '停电纪念日的记忆',
    '停電紀念日的記憶',
    'Memory of the anniversary power cut',
  ],
  'memory.life.cassette.alt': [
    '停电的纪念日里，中年的秀兰在录音机旁轻轻哼唱，志远在灯火下安静倾听',
    '停電的紀念日裡，中年的秀蘭在錄音機旁輕輕哼唱，志遠在燈火下安靜傾聽',
    'On the anniversary of a power cut, middle-aged Xiulan hums beside a cassette player while Zhiyuan listens in the lamplight',
  ],
  'memory.ending.hand.label': [
    '志远主动握住秀兰的手',
    '志遠主動牽住秀蘭的手',
    'Zhiyuan reaches for Xiulan’s hand',
  ],
  'memory.ending.hand.alt': [
    '志远从左侧主动把手覆在秀兰停下等待的掌心上，背景里放着仍然温热的面',
    '志遠從左側主動把手覆在秀蘭停下等待的掌心上，背景裡放著仍然溫熱的麵',
    'Zhiyuan places his hand over Xiulan’s waiting palm from the left, with still-warm noodles in the background',
  ],
  'journal.home.key.title': ['六月二十二日', '六月二十二日', 'June 22'],
  'journal.home.key.body': [
    '钥匙还在玄关的蓝色小碗里。别急，我去买面，很快回来。——秀兰',
    '鑰匙還在玄關的藍色小碗裡。別急，我去買麵，很快回來。——秀蘭',
    'The key is still in the blue bowl by the entrance. Don’t hurry. I’m going to buy noodles and will be right back. — Xiulan',
  ],
  'journal.rain.route.title': ['旧车站', '舊車站', 'The old station'],
  'journal.rain.route.body': [
    '第一次见到志远是在旧车站。先经过三个站牌，再听钟楼的三声响。',
    '第一次見到志遠是在舊車站。先經過三個站牌，再聽鐘樓的三聲響。',
    'I first met Zhiyuan at the old station. Pass three signs first, then listen for the three bells from the clock tower.',
  ],
  'journal.life.ordinary_days.title': ['许多普通的日子', '許多普通的日子', 'Many ordinary days'],
  'journal.life.ordinary_days.body': [
    '记不得年份，也不妨碍我们一起闻过花香。',
    '記不得年份，也不妨礙我們一起聞過花香。',
    'Not remembering the year does not change that we once smelled the flowers together.',
  ],
  'journal.return.last_page.title': ['最后一页', '最後一頁', 'The last page'],
  'journal.return.last_page.body': [
    '如果你一时叫不出我的名字，也没关系。我会再告诉你。你不需要证明自己还记得多少，才值得被好好陪着。——秀兰',
    '如果你一時叫不出我的名字，也沒關係。我會再告訴你。你不需要證明自己還記得多少，才值得被好好陪著。——秀蘭',
    'If my name does not come to you for a while, that is all right. I will tell you again. You do not have to prove how much you remember to deserve good company. — Xiulan',
  ],
  'map.room.bedroom': ['卧室', '臥室', 'Bedroom'],
  'map.room.living': ['客厅', '客廳', 'Living room'],
  'map.room.kitchen': ['厨房', '廚房', 'Kitchen'],
  'map.room.entry': ['玄关', '玄關', 'Entrance'],
  'map.area.platform': ['旧站台', '舊站台', 'Old platform'],
  'map.area.market': ['棚街', '棚街', 'Market lane'],
  'map.area.clock': ['钟表铺', '鐘錶舖', 'Clock shop'],
  'map.period.first': ['纸箱与新木纹', '紙箱與新木紋', 'Boxes and new wood grain'],
  'map.period.second': ['桂花与杯印', '桂花與杯印', 'Osmanthus and cup rings'],
  'map.period.third': ['灯火与旧木纹', '燈火與舊木紋', 'Lamplight and old wood grain'],
  'map.junction': ['当前路口', '目前路口', 'Current junction'],
  'map.home': ['熟悉的家', '熟悉的家', 'The familiar home'],
  'map.sound.bell': ['钟声方向', '鐘聲方向', 'Direction of the bell'],
  'map.marker.reached': ['已到达', '已到達', 'Reached'],
} as const satisfies Record<string, Entry>;

export type TranslationKey = keyof typeof translationEntries;

export function isTranslationKey(value: unknown): value is TranslationKey {
  return (
    typeof value === 'string' && Object.prototype.hasOwnProperty.call(translationEntries, value)
  );
}

type HintChapter = 'home' | 'rain' | 'life' | 'return';
type HintLevel = 1 | 2 | 3;

const hintKeys = {
  home: ['hint.home.1', 'hint.home.2', 'hint.home.3'],
  rain: ['hint.rain.1', 'hint.rain.2', 'hint.rain.3'],
  life: ['hint.life.1', 'hint.life.2', 'hint.life.3'],
  return: ['hint.return.1', 'hint.return.2', 'hint.return.3'],
} as const satisfies Record<HintChapter, readonly TranslationKey[]>;

export function hintKeyFor(chapter: HintChapter, level: HintLevel): TranslationKey {
  return hintKeys[chapter][level - 1];
}

function buildCatalog(index: number): Record<string, string> {
  return Object.fromEntries(
    Object.entries(translationEntries).map(([key, values]) => [key, values[index]]),
  );
}

export const catalogs: Record<Locale, Record<string, string>> = {
  'zh-CN': buildCatalog(0),
  'zh-HK': buildCatalog(1),
  en: buildCatalog(2),
};

export const translationKeys = Object.keys(translationEntries) as TranslationKey[];
